import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { OrderFormInfoData, RequisitionFormData } from '@/types/RequisitionFormTypes'
import uuidByString from 'uuid-by-string'

interface ExistingRequestPayload {
  orderFormInfo: OrderFormInfoData
  requisitionFormData: RequisitionFormData
}

export enum OrderStatus {
  NEW = 'new',
  DRAFT = 'draft',
  COMPLETE = 'complete',
}

interface TrackedOrderFormState {
  status: OrderStatus
  id?: string
  provider_id?: string
  patient_id?: string
  json_payload?: ExistingRequestPayload
  created_at?: Date
  updated_at?: Date
}

export async function createOrder(
  data: OrderFormInfoData,
  formData: RequisitionFormData,
  status: OrderStatus
): Promise<void> {
  const formPayload: TrackedOrderFormState = {
    status,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: formData,
    },
    provider_id: uuidByString(data.orderingProvider?.id || ''),
    patient_id: uuidByString(data.patient.id),
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.post('/database/orders', formPayload, headers)
  return response.json()
}

export async function getOrderByPatient(patientId: string): Promise<TrackedOrderFormState> {
  const patientUUID = uuidByString(patientId)

  const client = ProviderAPIClient.getInstance()
  const response = await client.get(`/database/orders/patient/${patientUUID}`)

  if (response.status === 404) {
    return {
      status: OrderStatus.NEW,
    }
  }
  return response.json()
}

export async function updateOrder(
  orderId: string,
  data: OrderFormInfoData,
  formData: RequisitionFormData,
  orderStatus: OrderStatus
): Promise<void> {
  const orderPayload: TrackedOrderFormState = {
    status: orderStatus,
    json_payload: {
      orderFormInfo: data,
      requisitionFormData: formData,
    },
    provider_id: uuidByString(data.orderingProvider?.id || ''),
    patient_id: uuidByString(data.patient.id),
  }

  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.put(`/database/orders/${orderId}`, orderPayload, headers)
  return response.json()
}

export async function deleteOrder(orderId: string): Promise<void> {
  const client = ProviderAPIClient.getInstance()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const response = await client.delete(`/database/orders/${orderId}`, headers)

  if (!response.ok) {
    throw new Error(`Failed to delete order: ${response.statusText}`)
  }

  return response.json()
}
